<script setup lang="ts">
import type { AcademyLink } from '#academy-virtual/types'
import type { AcademyContentFile } from '#academy/utils/nav'
import type { MarkdownRoot } from '@nuxt/content'
import { MDCRenderer as MDCRendererBase } from '#components'
import { Spinner } from '@forgd/ui-kit/spinner'
import { extractMarkdownSection } from '../utils/content'

export interface Options {
  path: AcademyLink
  disableHeadlineLink?: boolean
}

const props = defineProps<Options>()

const el = ref<HTMLElement | null>(null)
const [snippetPath = props.path, sectionId] = props.path.split('#')
// AcademyContentFile omits the recursive `body` tree (see nav.ts); directly queried pages still carry it
const snippet = (await queryCollection(props.path.startsWith('/academy') ? 'pages' : 'snippets').path(snippetPath).first()) as AcademyContentFile & { body?: MarkdownRoot }
if (sectionId && snippet.body?.value) {
  const extracted = extractMarkdownSection(snippet.body?.value, sectionId)
  if (!extracted) {
    if (import.meta.dev) {
      throw new Error(`Could not find section ${sectionId} in snippet ${props.path}`)
    }
  }
  else {
    snippet.body.value = extracted
  }
}

const components = computed(() => {
  // Don't add anchor links to the headline
  if (props.disableHeadlineLink) {
    return { h1: 'h1', h2: 'h2', h3: 'h3' }
  }
  return undefined
})

interface MDCRendererProps {
  value?: AcademyContentFile
  components?: Record<string, unknown>
}

// `@nuxtjs/mdc` types MDCRenderer with a required `body: MDCRoot`, but inside this
// <ContentRenderer> slot it is driven by the content file via `value`. Its published
// types don't describe that usage, so re-annotate the component with the props we pass.
// This is a plain annotation (no cast) — the real component is assignable to it.
const MDCRenderer: new () => { $props: MDCRendererProps } = MDCRendererBase
</script>

<template>
  <article ref="el" data-ui="AcademySnippetLoader" class="nuxt-content">
    <Spinner v-if="!snippet" size="md" aria-label="Loading content" />
    <ContentRenderer v-else :value="snippet">
      <MDCRenderer :value="snippet" :components="components" />
    </ContentRenderer>
  </article>
</template>
